import {
  createWarningPatternMutation,
  deleteWarningPatternMutation,
  updateWarningPatternMutation,
} from '@/api/warningPatterns/warningPatternMutations';
import type { WarningPattern } from '@/api/warningPatterns/warningPatternQueries';
import { getWarningPatternsQuery } from '@/api/warningPatterns/warningPatternQueries';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import type { WarningPatternFormValues } from './WarningPatternDialog';
import { WarningPatternDialog } from './WarningPatternDialog';

const PER_PAGE = 15;

export default function UserWarnings() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patternIdToDelete, setPatternIdToDelete] = useState<number | null>(
    null,
  );
  const [editingPattern, setEditingPattern] = useState<WarningPattern | null>(
    null,
  );

  const { data, isLoading } = useQuery(
    getWarningPatternsQuery(currentPage, PER_PAGE),
  );

  const patterns = data?.data ?? [];
  const meta = data?.meta;

  const createMutation = useMutation({
    ...createWarningPatternMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warning-patterns'] });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    ...updateWarningPatternMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warning-patterns'] });
      setDialogOpen(false);
      setEditingPattern(null);
    },
  });

  const deleteMutation = useMutation({
    ...deleteWarningPatternMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warning-patterns'] });
    },
  });

  const handleAddNew = () => {
    setEditingPattern(null);
    setDialogOpen(true);
  };

  const handleEdit = (pattern: WarningPattern) => {
    setEditingPattern(pattern);
    setDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setPatternIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (patternIdToDelete !== null) {
      deleteMutation.mutate(patternIdToDelete);
      setDeleteDialogOpen(false);
      setPatternIdToDelete(null);
    }
  };

  const handleSubmit = (values: WarningPatternFormValues) => {
    if (editingPattern) {
      updateMutation.mutate({
        id: editingPattern.id,
        name: values.name || null,
        surname: values.surname || null,
        email: values.email || null,
        type: values.type,
      });
    } else {
      createMutation.mutate({
        name: values.name || null,
        surname: values.surname || null,
        email: values.email || null,
        type: values.type,
      });
    }
  };

  const getMatchLabel = (type: boolean) => {
    return type ? 'Exact Match' : 'Part Match';
  };

  return (
    <>
      <DashboardTableAccordion
        value="User warnings"
        title="User warnings"
        data={patterns}
        isLoading={isLoading}
        emptyMessage="No warning patterns found"
        loadingMessage="Loading..."
        paginationMeta={meta}
        onPageChange={setCurrentPage}
        tableWrapper="card"
        renderHeader={() => (
          <>
            <TableHead className="w-16">Id</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Match</TableHead>
            <TableHead className="w-48" />
          </>
        )}
        renderRow={(pattern) => (
          <>
            <TableCell>{pattern.id}</TableCell>
            <TableCell>
              {pattern.name || pattern.surname
                ? `${pattern.name ?? ''} ${pattern.surname ?? ''}`.trim()
                : ''}
            </TableCell>
            <TableCell>{pattern.email ?? ''}</TableCell>
            <TableCell>{getMatchLabel(pattern.type)}</TableCell>
            <TableCell>
              <div className="flex justify-end gap-3">
                <RelationActionButton
                  type="button"
                  onClick={() => handleEdit(pattern)}
                >
                  Edit
                </RelationActionButton>
                <RelationActionButton
                  type="button"
                  onClick={() => handleDeleteClick(pattern.id)}
                  disabled={deleteMutation.isPending}
                  className="text-delete"
                >
                  Remove
                </RelationActionButton>
              </div>
            </TableCell>
          </>
        )}
        footerActions={
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleAddNew}
          >
            Add New Record
          </Button>
        }
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Remove warning pattern"
        description="Are you sure you want to remove this warning pattern?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />

      <WarningPatternDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pattern={editingPattern}
        onSubmit={handleSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
}
